/**
 * Copyright Mántum All Rights Reserved.
 */
import Action from './action';
import debug from 'debug';
import store from '../store';
import useSWR from 'swr';
import { source, helper, validator, accent } from '@mantum/kanela';
import { KEY_FROZEN } from '../../input/frozen';
import { KEY_TRACE } from '../../input/trace';

import {
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import {
    ACTION_DATASOURCE,
    ACTION_VALUE,
    EVENT_CHANGED,
    useGroup,
    useGroupDispatch
} from '../../../group';

import {
    KEY_CLOSE,
    KEY_CONNECTED,
    KEY_DETAIL,
    KEY_LABEL,
    KEY_ORIGINAL,
    KEY_SIMPLE,
    KEY_SOCKET_ADDRESS,
    KEY_SOCKET_PORT,
    KEY_STABLE_WEIGHING,
    KEY_VALUE
} from './constant';

import type { Content } from '../../../group';
import type { MultiValue, SelectInstance, SingleValue } from 'react-select';
import type { Render, LoadOptions } from './type';

import type {
    DataSource,
    Error,
    Event,
    Filter,
    Group,
    HttpError,
    Locale,
    Option,
    Options,
    Parameter,
    Result,
    Status,
    Validation,
    WebSocket
} from '@mantum/kanela';

// const rProperty = /\{[a-z]+\}/i;
const rProperty = /\{\w+(\.\w+)?\}/g;

const info = debug('kanela:select');
const logger = debug('kanela:websocket');

export type PropTypes = {
    advanced?: {
        allowAdding?: boolean;
        entity?: string;
        filter?: Filter;
        group?: Group;
        listener?: boolean;
        menuShouldScrollIntoView?: boolean;
        minWeight?: number;
        namespace?: string;
        options?: Option<string>;
        placement?: 'auto' | 'top' | 'bottom';
        position?: 'fixed' | 'absolute';
        preselect?: boolean;
        refresh?: number;
        result?: Result;
        revalidate?: boolean;
        storing?: boolean;
        webSocket?: WebSocket;
    };
    cacheOptions?: boolean;
    dataSource?: Options<unknown> | DataSource;
    disabled?: boolean;
    error?: boolean;
    event?: Event;
    instanceId?: number | string;
    loadOptions?: LoadOptions;
    loading?: boolean;
    onLoadOptions?: (dataSource?: DataSource) => void;
    locale?: Locale;
    multiple?: boolean;
    searchable?: boolean;
    name?: string;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (
        name?: string,
        value?: Option<unknown> | Options<unknown> | unknown
    ) => void;
    onMessage?: (
        title: string,
        errors?: HttpError | string[] | Error,
        success?: boolean,
        status?: Status
    ) => void;
    onListener?: (values?: Record<string, unknown>) => void;
    optional?: boolean;
    original?: unknown[];
    placeholder?: string;
    render?: Render;
    testid?: string;
    validation?: Validation;
    value?: Option<unknown> | Options<unknown>;
};

export default forwardRef<SelectInstance<Option<unknown>>, PropTypes>(
    function Component(props, ref) {
        const {
            advanced = {},
            disabled = false,
            multiple = false,
            name,
            onListener,
            original = [],
            render: Render,
            searchable = true
        } = props;

        const {
            allowAdding = false,
            listener = false,
            menuShouldScrollIntoView,
            namespace = '',
            placement = 'auto',
            position,
            preselect = false,
            refresh = 0,
            minWeight = 40,
            result = 'complete',
            revalidate = true,
            storing = false
        } = advanced;

        const config = {
            refreshInterval: refresh,
            revalidateOnFocus: revalidate
        };

        const groups = useGroup();
        const dispatch = useGroupDispatch();

        const emptyRef = useRef(true);
        const selectValueRef = useRef({});

        const changeRef = useRef(props.onChange);
        const loadOptionsRef = useRef(props.onLoadOptions);
        const originalDataSourceRef = useRef(props.dataSource);

        const originalRef = useRef<unknown[]>([]);
        const socketRef = useRef<globalThis.WebSocket>();
        const parameterRef = useRef<Record<string, Record<string, Parameter>>>(
            {}
        );

        const [errorWebSocket, setErrorWebSocket] = useState(false);
        const [titleErrorWebSocket, setTitleErrorWebSocket] = useState('');

        const [dataSource, setDataSource] = useState<
            Options<unknown> | DataSource
        >(props.dataSource || []);

        const [selectedOption, setSelectedOption] = useState<
            | MultiValue<Option<unknown>>
            | SingleValue<Option<unknown>>
            | undefined
            | null
        >();

        const fetcher = () => store(dataSource);

        const query = useMemo(() => {
            return !disabled &&
                !loadOptionsRef.current &&
                !Array.isArray(dataSource) &&
                !validator.isUndefined(dataSource.url)
                ? {
                      url: dataSource.url,
                      args: {
                          body: dataSource.parameter,
                          headers: dataSource.headers,
                          method: dataSource.method,
                          namespace: dataSource.namespace
                      }
                  }
                : null;
        }, [dataSource, disabled]);

        const { data } = useSWR(query, fetcher, config);

        const getOriginalDataSource = useCallback(() => {
            if (
                (Array.isArray(originalDataSourceRef.current) &&
                    originalDataSourceRef.current.length > 0) ||
                validator.isDataSource(originalDataSourceRef.current)
            ) {
                return originalDataSourceRef.current;
            }
            return null;
        }, []);

        const toSelect = useCallback(
            (values?: unknown[] | Options<unknown>): Options<unknown> => {
                if (!values) {
                    return [];
                }

                const label = props.advanced?.options?.label
                    .split('.')
                    .filter((it) => it !== '') ?? ['label'];

                const value = props.advanced?.options?.value
                    .split('.')
                    .filter((it) => it !== '') ?? ['value'];

                return helper.select.convert(values, label, value);
            },
            [props.advanced?.options]
        );

        const getValue = (selected: unknown) => {
            if (selected) {
                return typeof selected === 'object' && 'value' in selected
                    ? selected.value
                    : selected;
            }
            return null;
        };

        const getParameter = () => {
            const parameter: Record<string, string> = {};

            for (const [, target] of Object.entries(parameterRef.current)) {
                for (const [, origin] of Object.entries(target)) {
                    for (const [key, values] of Object.entries(origin)) {
                        if (!(key in parameter)) {
                            parameter[key] = values as string;
                        } else {
                            let value = parameter[key];
                            if (key !== 'format') {
                                if (value && typeof value === 'string') {
                                    const current = value.split(',');
                                    current.push(`${values}`);
                                    value = current.join(',');
                                }
                            }
                            parameter[key] = value;
                        }
                    }
                }
            }

            if (Object.keys(parameter).length === 0) {
                return null;
            }
            return parameter;
        };

        const getItemByValue = useCallback(
            (
                value: unknown,
                defaultValue: Record<string, unknown> | null = null
            ) => {
                const entity = props.advanced?.entity;
                if (entity) {
                    const original = originalRef.current;
                    for (let index = 0; index < original.length; index++) {
                        const element = original[index];
                        if (element && typeof element === 'object') {
                            const result = source.extract(element, entity);
                            if (result === value) {
                                return element;
                            }
                        }
                    }
                }

                return defaultValue;
            },
            [props.advanced?.entity]
        );

        const getSimpleValue = useCallback(
            (
                option?:
                    | MultiValue<Option<unknown>>
                    | SingleValue<Option<unknown>>
                    | null
            ) => {
                let current: unknown = option;
                const isNormal =
                    result === KEY_SIMPLE || result === KEY_ORIGINAL;

                if (current && isNormal) {
                    if (Array.isArray(current)) {
                        current = current.map((it) => {
                            return result === KEY_SIMPLE
                                ? it.value
                                : getItemByValue(it.value);
                        });
                    } else {
                        current = getValue(current);
                        if (current && result === KEY_ORIGINAL) {
                            current = getItemByValue(current);
                        }
                    }
                }

                return current;
            },
            [result, getItemByValue]
        );

        const onChange = useCallback(
            (
                name?: string,
                option?:
                    | MultiValue<Option<unknown>>
                    | SingleValue<Option<unknown>>
                    | null
            ) => {
                if (!changeRef.current) {
                    return;
                }

                const current = getSimpleValue(option);
                changeRef.current(name, current);

                if (storing) {
                    if (name && window && window.localStorage) {
                        info('storing value in local storage %o', option);
                        const key = `${namespace}/${name}`;

                        if (!current) {
                            window.localStorage.removeItem(key);
                            return;
                        }

                        window.localStorage.setItem(
                            key,
                            JSON.stringify(option)
                        );
                    }
                }
            },
            [getSimpleValue, storing, namespace]
        );

        const openConnection = useCallback(
            (selected: Option<unknown>) => {
                if (!props.advanced?.webSocket) {
                    return;
                }

                if (!selected) {
                    setErrorWebSocket(false);
                    return;
                }

                let target = props.advanced?.webSocket?.target;
                const setting = props.advanced?.webSocket?.setting;
                if (!target || !setting) {
                    logger('the target or setting is undefined');
                    return;
                }

                const key = getValue(selected);
                const value = getItemByValue(key);
                if (!value || !(setting in value)) {
                    logger('the original value is undefined');
                    return;
                }

                if (socketRef.current) {
                    return;
                }

                const data: Record<string, unknown> =
                    value[setting as keyof unknown];

                const host =
                    KEY_SOCKET_ADDRESS in data
                        ? data?.[KEY_SOCKET_ADDRESS]
                        : props.advanced.webSocket.host;

                const port =
                    KEY_SOCKET_PORT in data
                        ? data?.[KEY_SOCKET_PORT]
                        : props.advanced.webSocket.port;

                if (!host || !port) {
                    return;
                }

                logger('opening connection %s:%s', host, port);
                socketRef.current = new window.WebSocket(`${host}:${port}`);

                socketRef.current.onopen = function () {
                    logger('open connection');
                    setErrorWebSocket(false);

                    const title = KEY_LABEL in selected ? selected.label : '';

                    const instance = setInterval(() => {
                        const isOpen =
                            socketRef.current?.readyState ===
                            socketRef.current?.OPEN;

                        if (isOpen) {
                            clearInterval(instance);

                            logger('sending data %s', title);
                            socketRef.current?.send(
                                JSON.stringify({
                                    action: KEY_CONNECTED,
                                    data: {
                                        ...data,
                                        title
                                    }
                                })
                            );
                            setTitleErrorWebSocket(
                                'WebSocket connection successfully established'
                            );
                        }
                    }, 1000);
                };

                socketRef.current.onerror = function (event) {
                    logger('error connection: %o', event);
                    setErrorWebSocket(true);
                    setTitleErrorWebSocket(
                        'An error occurred in the connection'
                    );
                };

                socketRef.current.onclose = function () {
                    logger('the connection has been closed');

                    setErrorWebSocket(true);
                    setTitleErrorWebSocket('The connection has been closed');

                    logger('clear socket instances');
                    socketRef.current = undefined;

                    const value = selectValueRef.current as Option<unknown>;
                    const instance = setInterval(() => {
                        const isOpen =
                            socketRef.current &&
                            socketRef.current?.readyState ===
                                socketRef.current?.OPEN;

                        if (isOpen) {
                            logger('the connection is open!');
                            clearInterval(instance);
                            return;
                        }

                        logger('retrying connection');
                        if (value && !emptyRef.current) {
                            openConnection(value);
                        }
                    }, 1000);
                };

                const parse = (data: string) => {
                    try {
                        return JSON.parse(data);
                    } catch {
                        return null;
                    }
                };

                socketRef.current.onmessage = function (message) {
                    const received = parse(message.data);
                    if (!received) {
                        logger('the error parse message received ');
                        return;
                    }

                    const { body, error } = received;
                    const content: Content[] = [];

                    if (body) {
                        if (error) {
                            logger('error message received: %s', body);
                            setErrorWebSocket(true);
                            setTitleErrorWebSocket(body);

                            if (typeof target === 'string') {
                                target = [target];
                            }

                            if (Array.isArray(target)) {
                                Object.values(target).forEach((it) => {
                                    content.push({
                                        action: ACTION_VALUE,
                                        target: it,
                                        value: ''
                                    });
                                });
                            }
                        } else {
                            setErrorWebSocket(false);
                            setTitleErrorWebSocket('');

                            if (target) {
                                getMessageReceived(target, body).forEach(
                                    ([key, value]) => {
                                        if (!body?.value) {
                                            content.push({
                                                action: ACTION_VALUE,
                                                target: key,
                                                value: ''
                                            });
                                        }

                                        //prettier-ignore
                                        const keys = key === KEY_FROZEN || key === KEY_TRACE;
                                        if (keys) {
                                            // prettier-ignore
                                            const isValid = !(KEY_STABLE_WEIGHING in body) 
                                                || body?.[KEY_STABLE_WEIGHING] === false;

                                            if (isValid) {
                                                return;
                                            }
                                        }

                                        content.push({
                                            action: ACTION_VALUE,
                                            target: key,
                                            value: value
                                        });
                                    }
                                );
                            }

                            if (listener) {
                                // prettier-ignore
                                if (body?.value !== null && body?.value !== undefined) {
                                    const weight = Number(body?.value);
                                    if (!isNaN(weight)) {
                                        const isActive = weight > minWeight;
                                        dispatchEvent(
                                            new CustomEvent('onListener', {
                                                [KEY_DETAIL]: {
                                                    ...body,
                                                    isActive
                                                }
                                            })
                                        );
                                        logger(
                                            'Dispatch event with device data %o ',
                                            { ...body, isActive }
                                        );
                                    }

                                    logger(
                                        'The value is not a number %s ',
                                        weight
                                    );
                                }
                            }
                        }
                    }

                    if (content.length > 0) {
                        dispatch({
                            type: EVENT_CHANGED,
                            content,
                            merge: true
                        });
                    }
                };
            },
            [
                props.advanced?.webSocket,
                getItemByValue,
                dispatch,
                listener,
                minWeight
            ]
        );

        const closeConnection = useCallback(() => {
            if (!socketRef.current) {
                return;
            }

            if (socketRef.current.readyState === socketRef.current.OPEN) {
                logger('closing websocket');
                socketRef.current.send(
                    JSON.stringify({
                        action: KEY_CLOSE
                    })
                );

                socketRef.current.close();
            }

            let target = props.advanced?.webSocket?.target;
            if (target) {
                if (typeof target === 'object') {
                    target = Object.keys(target);
                }

                if (Array.isArray(target)) {
                    logger('clear the "%s" field', target);
                    dispatch({
                        type: EVENT_CHANGED,
                        content: target
                            .filter((it) => it !== KEY_FROZEN)
                            .map((it) => ({
                                action: ACTION_VALUE,
                                target: it,
                                value: ''
                            }))
                    });
                }
            }
        }, [props.advanced?.webSocket?.target, dispatch]);

        const isString = (dataSource: unknown) => {
            return (
                typeof dataSource === 'string' &&
                !validator.isDataSource(dataSource)
            );
        };

        const isDataSource = (dataSource: unknown) => {
            return (
                typeof dataSource === 'object' &&
                validator.isDataSource(dataSource)
            );
        };

        const dispatchFilter = useCallback(
            (
                filters: Filter[],
                current?: Option<unknown>,
                clear = true,
                defaultValue: Record<string, unknown> | null = null,
                merge = false
            ) => {
                const values = current
                    ? getItemByValue(current.value, defaultValue)
                    : null;

                const content = filters.map<Content>((filter) => {
                    info('apply filter to %s: %o', name, values);
                    if (!values) {
                        return {
                            action: ACTION_DATASOURCE,
                            origin: name,
                            previous: filter.previous,
                            target: filter.target,
                            value: []
                        };
                    }

                    let dataSource = null;
                    if (isString(filter.dataSource)) {
                        const value = `${filter.dataSource}`
                            .split('.')
                            .filter((it) => it !== '')
                            .reduce(
                                (previous, current) =>
                                    previous[current as keyof unknown],
                                values
                            );

                        if (Array.isArray(value)) {
                            dataSource = value;
                        }
                    } else if (isDataSource(filter.dataSource)) {
                        const result: Record<string, unknown> = {};
                        const current = filter.dataSource as DataSource;

                        let url = current.url;
                        const parameters = current?.parameter ?? {};

                        Object.keys(parameters).forEach((key) => {
                            const element = parameters[key];

                            const value =
                                typeof element === 'string'
                                    ? source.extract(values, element)
                                    : element;

                            result[key] = value || element;
                        });

                        if (rProperty.test(current.url)) {
                            const placeholders = current.url.match(rProperty);
                            if (placeholders) {
                                for (let i = 0; i < placeholders.length; i++) {
                                    const placeholder = placeholders[i];
                                    if (placeholder) {
                                        const namespace = placeholder.replace(
                                            /\{(.*?)\}/g,
                                            '$1'
                                        );

                                        const value = source.extract(
                                            values,
                                            namespace
                                        );

                                        if (value) {
                                            url = url.replace(
                                                placeholder,
                                                `${value}`
                                            );
                                        }
                                    }
                                }
                            }
                        }

                        dataSource = {
                            ...current,
                            url,
                            parameter: result
                        };
                    }

                    info('change datasource %o', dataSource);
                    return {
                        action: ACTION_DATASOURCE,
                        clear: clear,
                        origin: name,
                        previous: filter.previous,
                        target: filter.target,
                        value: dataSource
                    };
                });

                dispatch({
                    type: EVENT_CHANGED,
                    content,
                    merge
                });
            },
            [name, dispatch, getItemByValue]
        );

        const applyFilter = useCallback(
            (
                current?: Option<unknown>,
                clear = true,
                defaultValue: Record<string, unknown> | null = null,
                merge = false
            ) => {
                if (!props.advanced?.filter || props.disabled) {
                    return;
                }

                const filters = Array.isArray(props.advanced.filter)
                    ? props.advanced.filter
                    : [props.advanced.filter];

                const ValidateFilters =
                    Array.isArray(filters) && filters.every((it) => it?.merge);

                const validateMerge = ValidateFilters
                    ? ValidateFilters
                    : props.advanced.filter.merge;

                const isMerge = validateMerge ?? merge;

                if (isMerge && !current) {
                    return;
                }

                dispatchFilter(filters, current, clear, defaultValue, isMerge);
            },
            [dispatchFilter, props.disabled, props.advanced?.filter]
        );

        const requestDispatch = (current?: unknown) => {
            const request = props.event?.change?.request;
            if (!request || props.disabled) {
                return;
            }

            if (!current || Array.isArray(current)) {
                const contents: Content[] = [];
                request.dispatch.forEach((it) => {
                    contents.push({
                        action: ACTION_VALUE,
                        target: it.name,
                        value: ''
                    });
                });

                const group = props.advanced?.group;
                if (group) {
                    Object.keys(group).forEach((it) => {
                        contents.push({
                            action: ACTION_VALUE,
                            target: it,
                            value: ''
                        });
                    });
                }

                if (contents.length > 0) {
                    dispatch({
                        type: EVENT_CHANGED,
                        content: contents
                    });
                }
                return;
            }

            const value =
                typeof current === 'object' && 'value' in current
                    ? current.value
                    : current;

            const item = getItemByValue(value);
            if (item) {
                const dataSource = {
                    ...request.dataSource
                };

                Object.keys(item).forEach((key) => {
                    dataSource.url = dataSource.url.replace(
                        `{${key}}`,
                        `${item[key as keyof unknown]}`
                    );
                });

                const contents: Content[] = [];
                store(dataSource).then((results) => {
                    request.dispatch.forEach((it) => {
                        const value = source.extract(results, it.value);

                        contents.push({
                            action: ACTION_VALUE,
                            target: it.name,
                            value: value ? value : ''
                        });
                    });

                    if (contents.length > 0) {
                        dispatch({
                            type: EVENT_CHANGED,
                            content: contents
                        });
                    }
                });
            }
        };

        const dispatchGroup = (
            values?: MultiValue<Option<unknown>> | SingleValue<Option<unknown>>
        ) => {
            const group = props.advanced?.group;
            if (!group || props.disabled) {
                return;
            }

            let items: unknown[] = [];
            if (Array.isArray(values)) {
                items = values
                    .map((option) => getItemByValue(option.value))
                    .filter((it) => it);
            } else {
                if (values) {
                    const options = values as Option<unknown>;
                    const item = getItemByValue(options.value);
                    if (item) {
                        items.push(item);
                    }
                }
            }

            const contents: Content[] = [];
            Object.keys(group).forEach((it) => {
                if (items.length === 0) {
                    contents.push({
                        action: ACTION_VALUE,
                        target: it,
                        value: ''
                    });
                    return;
                }

                let value: unknown = null;
                const properties = group[it];

                if (typeof properties === 'string') {
                    items.forEach((item) => {
                        const current = source.extract(item, properties);
                        if (!current) {
                            return;
                        }

                        value =
                            typeof value === 'number' &&
                            typeof current === 'number'
                                ? value + current
                                : current;
                    });
                } else if (typeof properties === 'object') {
                    if (!validator.isDataSource(properties)) {
                        items.forEach((item) => {
                            if (!item || typeof item !== 'object') {
                                return;
                            }

                            const copy: Record<string, unknown> = {
                                ...properties
                            };

                            Object.keys(copy).forEach((it) => {
                                const template = copy[it];
                                if (template && typeof template === 'string') {
                                    const value = source.extract(
                                        item,
                                        template
                                    );
                                    copy[it] = value ? value : template;
                                }
                            });

                            value = value !== null ? [value, copy] : copy;
                        });
                    }
                }

                contents.push({
                    action: ACTION_VALUE,
                    target: it,
                    value: value ? value : ''
                });
            });

            if (contents.length > 0) {
                dispatch({
                    type: EVENT_CHANGED,
                    content: contents
                });
            }
        };

        useEffect(() => {
            const handlerOnListener = (event: CustomEvent) => {
                if (Object.keys(event?.[KEY_DETAIL]).length === 0) {
                    logger('Not data provided');
                    return;
                }

                if (onListener) {
                    onListener({ ...event?.[KEY_DETAIL] });
                }
            };

            window.addEventListener(
                'onListener',
                handlerOnListener as EventListener
            );

            return () => {
                window.removeEventListener(
                    'onListener',
                    handlerOnListener as EventListener
                );
            };
        }, [onListener, minWeight]);

        useEffect(() => {
            changeRef.current = props.onChange;
        }, [props.onChange]);

        useEffect(() => {
            originalDataSourceRef.current = props.dataSource;
        }, [props.dataSource]);

        useEffect(() => {
            loadOptionsRef.current = props.onLoadOptions;
        }, [props.onLoadOptions]);

        useEffect(() => {
            if (Array.isArray(dataSource) && dataSource.length > 0) {
                originalRef.current = dataSource;
                return;
            }

            if (Array.isArray(data) && data.length > 0) {
                originalRef.current = data;
                return;
            }

            if (Array.isArray(original) && original.length > 0) {
                originalRef.current = original;
                return;
            }

            originalRef.current = [];
        }, [dataSource, data, original]);

        useEffect(() => {
            info('refresh: new datasource');
            setDataSource(props.dataSource || []);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [JSON.stringify(props.dataSource)]);

        useEffect(() => {
            if (changeRef.current) {
                changeRef.current(name, getSimpleValue(props.value));
            }
        }, [name, getSimpleValue, props.value]);

        useEffect(() => {
            if (props.value) {
                info('default value %o', props.value);
                setSelectedOption(props.value);
                if (!Array.isArray(props.value)) {
                    applyFilter(props.value, false, props.value, true);
                }
                return;
            }
            setSelectedOption(null);
        }, [props.value, applyFilter]);

        useEffect(() => {
            if (!storing) {
                return;
            }

            if (!name || !window || !window.localStorage) {
                return;
            }

            let intervalId: NodeJS.Timeout | null = null;

            const key = `${namespace}/${name}`;
            const value = window.localStorage.getItem(key);
            if (value) {
                try {
                    const selected = JSON.parse(value);
                    if (selected) {
                        info('load value from local storage %o', selected);

                        setSelectedOption(selected);
                        if (changeRef.current) {
                            changeRef.current(name, getSimpleValue(selected));
                        }

                        if (!Array.isArray(selected)) {
                            intervalId = setTimeout(() => {
                                if (originalRef.current.length > 0) {
                                    applyFilter(selected, false);
                                    if (intervalId) {
                                        clearInterval(intervalId);
                                    }
                                }
                            }, 500);
                        }
                    }
                } catch {
                    // do nothing.
                }

                return () => {
                    if (intervalId) {
                        clearInterval(intervalId);
                    }
                };
            }
        }, [name, getSimpleValue, storing, applyFilter, namespace]);

        useEffect(() => {
            if (!name || !groups) {
                return;
            }

            if (!(name in groups)) {
                return;
            }

            info('refresh: receive dispatch');

            const content = groups[name];
            if (content.action === ACTION_VALUE) {
                info('[dispatch]: %s change value %o', name, content.value);

                const value = content.value
                    ? (content.value as Option<unknown>)
                    : null;

                setSelectedOption(value);
                onChange(name, value);

                if (!value) {
                    logger('the value is empty');
                    closeConnection();
                }

                if (name && name in parameterRef.current) {
                    delete parameterRef.current[name];
                    if (loadOptionsRef.current) {
                        loadOptionsRef.current(undefined);
                    }
                }
            } else if (content.action === ACTION_DATASOURCE) {
                info('[dispatch]: field %s change datasource', name);

                if (Array.isArray(content.value)) {
                    let value: DataSource | Options<unknown> = content.value;
                    if (value.length === 0) {
                        const dataSource = getOriginalDataSource();
                        if (dataSource) {
                            value = dataSource;
                        }
                    }

                    if (content.previous && content.origin) {
                        if (name && name in parameterRef.current) {
                            const target = parameterRef.current[name];
                            if (target && content.origin in target) {
                                delete target[content.origin];
                            }
                        }
                    }

                    setDataSource((previous) => {
                        if (content.previous) {
                            const parameter = getParameter();
                            if (parameter) {
                                const dataSource = {
                                    ...previous,
                                    parameter: parameter
                                };

                                if (loadOptionsRef.current) {
                                    if (validator.isDataSource(dataSource)) {
                                        loadOptionsRef.current(
                                            dataSource as DataSource
                                        );
                                    }
                                }

                                return dataSource;
                            }
                        }

                        if (loadOptionsRef.current) {
                            loadOptionsRef.current(undefined);
                        }

                        return value;
                    });

                    if (content.clear) {
                        info('clear selection');
                        setSelectedOption(null);
                        onChange(name, null);

                        applyFilter(undefined);
                    }
                } else if (validator.isDataSource(content.value)) {
                    const dataSource = content.value as DataSource;
                    if (content.origin && content.previous) {
                        info(
                            '[%s] persists previous parameters',
                            content.origin
                        );

                        parameterRef.current = {
                            ...parameterRef.current,
                            [name]: {
                                ...parameterRef.current[name],
                                [content.origin]: dataSource.parameter ?? {}
                            }
                        };

                        const parameter = getParameter();
                        if (parameter) {
                            info('[%s] generate parameter %o', name, parameter);
                            dataSource.parameter = parameter;
                        }
                    }

                    if (loadOptionsRef.current) {
                        if (validator.isDataSource(dataSource)) {
                            loadOptionsRef.current(dataSource);
                        }
                    }

                    info('apply new datasource %o', dataSource);
                    setDataSource(dataSource);

                    if (content.clear) {
                        info('clear selection');
                        setSelectedOption(null);
                        onChange(name, null);
                    }
                }
            }
        }, [
            applyFilter,
            closeConnection,
            getOriginalDataSource,
            groups,
            name,
            onChange
        ]);

        useEffect(() => {
            if (!preselect) {
                return;
            }

            const selected = data ?? dataSource ?? [];
            if (!Array.isArray(selected) || selected.length === 0) {
                return;
            }

            const items = toSelect(selected);
            if (items.length === 0) {
                return;
            }

            const item = items[0];
            info('preselect value %o', item);
            if (changeRef.current) {
                changeRef.current(name, getSimpleValue(item));
            }

            setSelectedOption((previous) => {
                if (!previous) {
                    return item;
                }
                return previous;
            });

            selectValueRef.current = item;
            emptyRef.current = !item || !(KEY_VALUE in item);

            openConnection(item);
            return () => {
                if (preselect) {
                    info('destroy component');
                    closeConnection();
                }
            };
        }, [
            closeConnection,
            data,
            dataSource,
            getSimpleValue,
            name,
            openConnection,
            preselect,
            toSelect
        ]);

        const handleChange = (
            selected: MultiValue<Option<unknown>> | SingleValue<Option<unknown>>
        ) => {
            info('change input value for %s', name);
            setSelectedOption(selected);
            onChange(name, selected);

            dispatchGroup(selected);
            requestDispatch(selected);

            if (!Array.isArray(selected)) {
                const current = selected as Option<unknown>;
                applyFilter(current);

                emptyRef.current = !current || !(KEY_VALUE in current);
                selectValueRef.current = current;

                closeConnection();
                openConnection(current);
            }
        };

        const getMessageReceived = (
            target: string | string[],
            values: Record<string, unknown>
        ): [string, unknown][] => {
            if (!values) {
                return [];
            }

            if (!Array.isArray(target)) {
                target = [target];
            }

            if (Array.isArray(target)) {
                const results: [string, unknown][] = [];
                target.forEach((it) => {
                    for (const [key, namespace] of Object.entries(it)) {
                        const current = source.extract<unknown>(
                            values,
                            namespace
                        );

                        if (current !== null || current !== undefined) {
                            results.push([key, current]);
                        }
                    }
                });
                return results;
            }
            return [];
        };

        const reconnectWebSocket = () => {
            if (!selectedOption) {
                logger('the selected value is undefined');
                return;
            }

            if (!Array.isArray(selectedOption)) {
                logger('the reconnect websocket');

                const current = selectedOption as Option<unknown>;
                closeConnection();
                openConnection(current);
            }
        };

        const isValidNewOption = (inputValue: string) => {
            if (!allowAdding || validator.isEmpty(inputValue)) {
                return false;
            }

            if (!Array.isArray(dataSource)) {
                return false;
            }

            const result = dataSource?.find((it) =>
                accent(it.label).includes(accent(inputValue))
            );

            if (result) {
                return false;
            }

            return true;
        };

        const items = toSelect(!Array.isArray(dataSource) ? data : dataSource);

        const menuPortalTarget =
            position !== undefined && position === 'fixed'
                ? document.body
                : null;

        if (!Render) {
            return null;
        }

        return (
            <div className='flex w-full flex-row flex-nowrap items-center gap-2'>
                <Render
                    className='w-full text-base'
                    styles={{
                        control: (baseStyles) => ({
                            ...baseStyles,
                            backgroundColor: 'none',
                            borderColor: 'none',
                            boxShadow: 'none',
                            borderWidth: 'none',
                            '&:hover': {
                                borderColor: 'none'
                            }
                        }),
                        option: (baseSytles) => ({
                            ...baseSytles,
                            backgroundColor: 'none',
                            color: 'none',
                            '&:active': {
                                backgroundColor: 'var(--mantum-accent, #f3f4f6)'
                            }
                        }),
                        singleValue: (baseSytles) => ({
                            ...baseSytles,
                            color: 'none'
                        }),
                        menuPortal: (baseSytles) => ({
                            ...baseSytles,
                            zIndex: 9999
                        })
                    }}
                    classNames={{
                        control: (state) => {
                            const border = state.isFocused
                                ? 'border-2 border-primary'
                                : '';

                            const classNames = props.disabled
                                ? 'bg-transparent'
                                : '';

                            return `${
                                props.error
                                    ? 'border-2 border-error'
                                    : 'border border-gray-300'
                            } ${classNames} h-10 ${border}`;
                        },
                        placeholder: () => {
                            return 'truncate';
                        },
                        option: (state) => {
                            return state.isFocused || state.isSelected
                                ? 'text-slate-600 bg-gray-100'
                                : 'transparent';
                        },
                        singleValue: () => {
                            return 'text-gray-600';
                        }
                    }}
                    cacheOptions={props.cacheOptions}
                    classNamePrefix={
                        props.instanceId ? `${props.instanceId}` : undefined
                    }
                    form={props.testid}
                    id={props.testid}
                    instanceId={props.instanceId}
                    isClearable={props.error ? false : true}
                    isDisabled={props.disabled}
                    isLoading={!props.error ? !items : false}
                    isMulti={multiple}
                    isSearchable={searchable}
                    isValidNewOption={isValidNewOption}
                    loadOptions={props.loadOptions}
                    menuPlacement={placement}
                    menuPortalTarget={menuPortalTarget}
                    menuPosition={position}
                    menuShouldScrollIntoView={menuShouldScrollIntoView}
                    name={props.name}
                    onBlur={props.onBlur}
                    onChange={handleChange}
                    options={items}
                    placeholder={props.placeholder}
                    ref={ref}
                    value={selectedOption}
                />
                {!props.error &&
                    props.advanced?.webSocket &&
                    selectedOption !== null && (
                        <Action
                            error={errorWebSocket}
                            onClick={reconnectWebSocket}
                            title={titleErrorWebSocket}
                        />
                    )}
            </div>
        );
    }
);
